/**
 * Edit Milk Production - Editable form for milk production records
 * Allows users to update milk production details and save changes
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Loader, AlertCircle, CheckCircle } from 'lucide-react';
import { milkProductionApi, shedApi, type MilkRecord, type Shed } from '../../../services/gaushala/api';

export default function EditMilkProduction() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [sheds, setSheds] = useState<Shed[]>([]);
  const [loadingSheds, setLoadingSheds] = useState(true);
  const [formData, setFormData] = useState<MilkRecord>({
    id: undefined,
    gaushalaId: 0,
    entryType: '',
    shedNumber: '',
    milkQuantity: 0,
    fatPercentage: 0,
    snf: 0,
    notes: '',
    status: 'RECORDED',
  });

  useEffect(() => {
    loadRecord();
    loadSheds();
  }, [id]);

  const loadRecord = async () => {
    setLoading(true);
    setError(null);
    try {
      if (!id) {
        setError('No record ID provided');
        setLoading(false);
        return;
      }

      console.log('🥛 Loading milk record for edit - ID:', id);
      const response = await milkProductionApi.getMilkRecordById(parseInt(id));
      console.log('🥛 API Response:', response);

      if (response.success && response.data) {
        console.log('✅ Record loaded successfully:', response.data);
        setFormData(response.data);
      } else {
        console.error('❌ Failed to load record:', response.error);
        setError(response.error || 'Failed to load record');
      }
    } catch (err) {
      console.error('❌ Error loading record:', err);
      setError('Error loading record details: ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setLoading(false);
    }
  };

  const loadSheds = async () => {
    setLoadingSheds(true);
    try {
      // Load list of sheds
      const response = await shedApi.getAllSheds(0, 100);
      if (response.success && response.data) {
        // Filter only active sheds
        const activeSheds = response.data.content.filter(
          (shed) => shed.status === 'ACTIVE'
        );
        setSheds(activeSheds);
      }
    } catch (error) {
      console.error('Error loading sheds:', error);
    } finally {
      setLoadingSheds(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // Convert numeric fields
    let convertedValue: any = value;

    // Special handling for numeric fields to remove leading zeros
    if (['milkQuantity', 'fatPercentage', 'snf'].includes(name)) {
      const numericValue = value.replace(/[^0-9]/g, '');
      if (numericValue === '') {
        convertedValue = 0;
      } else {
        // parseInt automatically removes leading zeros: "05" becomes 5
        const numValue = parseInt(numericValue, 10);
        // Validate ranges
        if (name === 'milkQuantity' && numValue > 1000) {
          convertedValue = 1000;
        } else if ((name === 'fatPercentage' || name === 'snf') && numValue > 100) {
          convertedValue = 100;
        } else {
          convertedValue = numValue;
        }
      }
    } else if (name === 'gaushalaId') {
      convertedValue = parseInt(value) || 0;
    }

    setFormData(prev => ({
      ...prev,
      [name]: convertedValue
    }));

    console.log(`Field ${name} changed to:`, convertedValue);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      console.log('💾 Saving milk record:', formData);

      // Validate required fields
      if (!formData.shedNumber || !formData.shedNumber.trim()) {
        throw new Error('Shed number is required');
      }
      if (!formData.milkQuantity || formData.milkQuantity <= 0) {
        throw new Error('Milk quantity must be greater than 0');
      }

      const response = await milkProductionApi.updateMilkRecord(formData.id!, formData);
      console.log('💾 Save response:', response);

      if (response.success) {
        console.log('✅ Record saved successfully');
        setSuccess(true);

        // Show success message for 2 seconds then navigate
        setTimeout(() => {
          navigate(`/gaushala/production/view/${formData.id}`);
        }, 2000);
      } else {
        throw new Error(response.error || 'Failed to save record');
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : String(err);
      console.error('❌ Error saving record:', errorMsg);
      setError(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/gaushala/production/view/${id}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading milk record...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate('/gaushala/production')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={20} />
          Back to Records
        </button>
        <h1 className="text-2xl font-bold">Edit Milk Production Record</h1>
        <div className="w-20"></div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-4">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-red-900">Error</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Success Alert */}
      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-4">
          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-green-900">Success</h3>
            <p className="text-sm text-green-700 mt-1">Record saved successfully. Redirecting...</p>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Record ID (Read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Record ID</label>
            <input
              type="text"
              value={formData.id || ''}
              disabled
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
            />
          </div>

          {/* Shed Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Shed Name <span className="text-red-600">*</span>
            </label>
            {loadingSheds ? (
              <div className="text-sm text-gray-500">Loading sheds...</div>
            ) : (
              <select
                name="shedNumber"
                value={formData.shedNumber || ''}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a shed</option>
                {sheds.map((shed) => (
                  <option key={shed.id} value={shed.shedNumber}>
                    {shed.shedName} ({shed.shedNumber}) - Capacity: {shed.currentOccupancy}/{shed.capacity}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Entry Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Entry Type</label>
            <input
              type="text"
              name="entryType"
              value={formData.entryType || ''}
              onChange={handleInputChange}
              placeholder="e.g., Morning, Evening"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Milk Quantity */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Milk Quantity (L) <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              inputMode="numeric"
              name="milkQuantity"
              value={formData.milkQuantity || 0}
              onChange={handleInputChange}
              placeholder="Enter liters (0-1000)"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Fat Percentage */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Fat Percentage (%)</label>
            <input
              type="text"
              inputMode="numeric"
              name="fatPercentage"
              value={formData.fatPercentage || 0}
              onChange={handleInputChange}
              placeholder="Enter percentage (0-100)"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* SNF */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">SNF (Solids-Not-Fat)</label>
            <input
              type="text"
              inputMode="numeric"
              name="snf"
              value={formData.snf || 0}
              onChange={handleInputChange}
              placeholder="Enter percentage (0-100)"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              name="status"
              value={formData.status || 'RECORDED'}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="RECORDED">Recorded</option>
              <option value="VERIFIED">Verified</option>
              <option value="PROCESSED">Processed</option>
            </select>
          </div>

          {/* Notes */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
            <textarea
              name="notes"
              value={formData.notes || ''}
              onChange={handleInputChange}
              placeholder="Additional notes or observations..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Created/Updated Info */}
        <div className="mt-6 pt-6 border-t grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
          {formData.createdAt && (
            <div>
              <p className="font-medium">Created At</p>
              <p>{new Date(formData.createdAt).toLocaleString('en-IN')}</p>
            </div>
          )}
          {formData.updatedAt && (
            <div>
              <p className="font-medium">Updated At</p>
              <p>{new Date(formData.updatedAt).toLocaleString('en-IN')}</p>
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="mt-8 flex gap-4 justify-center">
          <button
            onClick={handleCancel}
            disabled={saving}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || success}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {saving ? (
              <>
                <Loader size={20} className="animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save size={20} />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
